import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Alert, FlatList, RefreshControl } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import ScreenWrapper from '../../Components/ScreenWrapper';
import { theme } from '../../constants/theme';
import { hp, wp } from '../../helpers/common';
import { getReports, updateReportStatus, getReportStats, hidePost } from '../../services/reportService';
import { useAuth } from '../../Utils/AuthProvider';
import Loading from '../../Components/Loading';
import { canAccessAdminFeatures } from '../../services/userService';
import { Supabase } from '../../Utils/supabase.config';

interface Report {
  id: string;
  reason: string;
  status: string;
  created_at: string;
  admin_notes?: string;
  reporter_id: string;
  reviewed_by?: string;
  posts: {
    id: string;
    body: string;
    file?: string;
    created_at: string;
    userId: string;
  };
}

export default function AdminReports() {
  const navigation = useNavigation<any>();
  const { user } = useAuth();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [stats, setStats] = useState<any>({});
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [authorized, setAuthorized] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [pageSize] = useState(20);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    checkAuthorization();
  }, [user]);

  const checkAuthorization = async () => {
    if (!user?.id) {
      navigation.goBack();
      return;
    }

    const result = await canAccessAdminFeatures(user.id);
    if (result.success && result.data) {
      setAuthorized(true);
      loadReports();
      loadStats();
    } else {
      Alert.alert(
        'Access Denied',
        'Only administrators can view reports.',
        [{ text: 'OK', onPress: () => navigation.goBack() }]
      );
    }
  };

  // Load reports when status filter changes (only if authorized)
  useEffect(() => {
    if (authorized) {
      setCurrentPage(0);
      setReports([]);
      setHasMore(true);
      loadReports();
    }
  }, [selectedStatus, authorized]);

  const loadReports = async (isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const offset = isLoadMore ? currentPage * pageSize : 0;
      const options: any = selectedStatus !== 'all' 
        ? { status: selectedStatus, limit: pageSize, offset, userId: user?.id } 
        : { limit: pageSize, offset, userId: user?.id };
      
      const result: any = await getReports(options);
      
      if (result.success) {
        if (isLoadMore) {
          setReports(prevReports => [...prevReports, ...result.data]);
          setCurrentPage(prevPage => prevPage + 1);
        } else {
          setReports(result.data);
          setCurrentPage(1);
        }
        setHasMore(result.data.length === pageSize);
      } else {
        Alert.alert('Error', result.msg || 'Failed to load reports');
      }
    } catch (error) {
      console.error('Error loading reports:', error);
      Alert.alert('Error', 'Failed to load reports');
    }
    
    setLoading(false);
    setLoadingMore(false);
  };

  const loadMoreReports = () => {
    if (!loadingMore && hasMore) {
      loadReports(true);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    setCurrentPage(0);
    setReports([]);
    setHasMore(true);
    await loadReports();
    await loadStats();
    setRefreshing(false);
  };

  const loadStats = async () => {
    try {
      const result: any = await getReportStats(user?.id);
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Update stats when reports change (for real-time count updates)
  const updateStatsFromReports = () => {
    if (reports.length > 0) {
      const newStats = {
        total_reports: reports.length,
        pending_reports: reports.filter(r => r.status === 'pending').length,
        accepted_reports: reports.filter(r => r.status === 'accepted').length,
        rejected_reports: reports.filter(r => r.status === 'rejected').length,
      };
      
      // Only update if stats actually changed to prevent unnecessary re-renders
      setStats((prev: any) => {
        if (JSON.stringify(prev) !== JSON.stringify(newStats)) {
          return newStats;
        }
        return prev;
      });
    }
  };

  // Update stats whenever reports change
  useEffect(() => {
    updateStatsFromReports();
  }, [reports]);

  // Real-time subscription for reports updates
  useEffect(() => {
    if (!user || !authorized) return;

    const reportsChannel = Supabase
      .channel('admin-reports-realtime')
      .on('postgres_changes' as any, { 
        event: '*', 
        schema: 'public', 
        table: 'reports' 
      } as any, (payload: any) => {
        // Update stats in real-time when reports change
        if (payload.eventType === 'INSERT') {
          // New report added
          setStats((prev: any) => ({
            ...prev,
            total_reports: (prev.total_reports || 0) + 1,
            pending_reports: (prev.pending_reports || 0) + 1
          }));
        } else if (payload.eventType === 'UPDATE') {
          // Report status changed
          const oldStatus = payload.old?.status;
          const newStatus = payload.new?.status;
          
          if (oldStatus && newStatus && oldStatus !== newStatus) {
            setStats((prev: any) => ({
              ...prev,
              [`${oldStatus}_reports`]: Math.max(0, (prev[`${oldStatus}_reports`] || 0) - 1),
              [`${newStatus}_reports`]: (prev[`${newStatus}_reports`] || 0) + 1
            }));
          }
        } else if (payload.eventType === 'DELETE') {
          // Report deleted
          const deletedStatus = payload.old?.status;
          if (deletedStatus) {
            setStats((prev: any) => ({
              ...prev,
              total_reports: Math.max(0, (prev.total_reports || 0) - 1),
              [`${deletedStatus}_reports`]: Math.max(0, (prev[`${deletedStatus}_reports`] || 0) - 1)
            }));
          }
        }
      })
      .subscribe();

    return () => {
      Supabase.removeChannel(reportsChannel);
    };
  }, [user, authorized]);

  const handleUpdateStatus = async (reportId: string, newStatus: string, adminNotes?: string) => {
    try {
      const result: any = await updateReportStatus(reportId, {
        status: newStatus,
        adminNotes: adminNotes || '',
        reviewedBy: user?.id
      });

      if (result.success) {
        // If report is accepted, hide the post
        if (newStatus === 'accepted') {
          const report = reports.find(r => r.id === reportId);
          if (report) {
            const hideResult: any = await hidePost(report.posts.id, user?.id);
            if (hideResult.success) {
              Alert.alert('Success', 'Report accepted and post hidden successfully');
            } else {
              Alert.alert('Warning', 'Report accepted but failed to hide post: ' + hideResult.msg);
            }
          }
        } else {
          Alert.alert('Success', 'Report status updated successfully');
        }
        
        loadReports();
        loadStats();
      } else {
        Alert.alert('Error', result.msg || 'Failed to update report status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      Alert.alert('Error', 'Failed to update report status');
    }
  };

  const showActionOptions = (report: Report) => {
    Alert.alert(
      'Report Actions',
      `Post: ${report.posts.body?.substring(0, 50)}...\nReason: ${report.reason}`,
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Accept Report & Hide Post', 
          style: 'destructive',
          onPress: () => {
            Alert.alert(
              'Accept Report',
              'This will hide the reported post from all users. Are you sure?',
              [
                { text: 'Cancel', style: 'cancel' },
                { 
                  text: 'Accept & Hide', 
                  style: 'destructive',
                  onPress: () => handleUpdateStatus(report.id, 'accepted', 'Report accepted - post hidden')
                }
              ]
            );
          }
        },
        { 
          text: 'Reject Report', 
          onPress: () => handleUpdateStatus(report.id, 'rejected', 'Report rejected by admin')
        }
      ]
    );
  };

  const renderReportItem = ({ item }: { item: Report }) => (
    <TouchableOpacity 
      style={[styles.reportItem, { borderLeftColor: getStatusColor(item.status) }]}
      onPress={() => showActionOptions(item)}
    >
      <View style={styles.reportHeader}>
        <Text style={styles.reportReason}>{item.reason}</Text>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
          <Text style={styles.statusText}>{item.status}</Text>
        </View>
      </View>
      
      <Text style={styles.postContent} numberOfLines={2}>
        {item.posts.body || 'No content'}
      </Text>
      
      <View style={styles.reportFooter}>
        <Text style={styles.reporterText}>Reported by: {item.reporter_id}</Text>
        <Text style={styles.dateText}>
          {new Date(item.created_at).toLocaleDateString()}
        </Text>
      </View>
    </TouchableOpacity>
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return theme.colors.warning || '#FFA500';
      case 'accepted': return theme.colors.success || '#28A745';
      case 'rejected': return theme.colors.grayDark || '#6C757D';
      default: return theme.colors.textLight || '#6C757D';
    }
  };

  const filterButtons = [
    { key: 'all', label: 'All', count: stats.total_reports || 0 },
    { key: 'pending', label: 'Pending', count: stats.pending_reports || 0 },
    { key: 'accepted', label: 'Accepted', count: stats.accepted_reports || 0 },
    { key: 'rejected', label: 'Rejected', count: stats.rejected_reports || 0 },
  ];

  if (!authorized) {
    return (
      <ScreenWrapper>
        <View style={styles.loadingContainer}>
          <Loading />
        </View>
      </ScreenWrapper>
    );
  }

  if (loading && reports.length === 0) {
    return (
      <ScreenWrapper>
        <View style={styles.loadingContainer}>
          <Loading />
        </View>
      </ScreenWrapper>
    );
  }

  return (
    <ScreenWrapper>
      <View style={styles.container}>
        <Text style={styles.title}>Report Management</Text>
        
        {/* Stats Overview */}
        <View style={styles.statsContainer}>
          {filterButtons.map((filter) => (
            <TouchableOpacity
              key={filter.key}
              style={[
                styles.statButton,
                selectedStatus === filter.key && styles.statButtonSelected
              ]}
              onPress={() => setSelectedStatus(filter.key)}
            >
              <Text style={[
                styles.statCount,
                selectedStatus === filter.key && styles.statCountSelected
              ]}>
                {stats[`${filter.key === 'all' ? 'total' : filter.key}_reports`] || 0}
              </Text>
              <Text style={[
                styles.statLabel,
                selectedStatus === filter.key && styles.statLabelSelected
              ]}>
                {filter.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Reports List */}
        <FlatList
          data={reports}
          renderItem={renderReportItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContainer}
          showsVerticalScrollIndicator={false}
          onEndReached={loadMoreReports}
          onEndReachedThreshold={0.5}
          onRefresh={onRefresh}
          refreshing={refreshing}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>No reports found</Text>
            </View>
          }
          ListFooterComponent={
            loadingMore ? (
              <View style={styles.loadingMoreContainer}>
                <Loading />
              </View>
            ) : !hasMore && reports.length > 0 ? (
              <Text style={styles.noMoreData}>No more reports</Text>
            ) : null
          }
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        />
      </View>
    </ScreenWrapper>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: wp(4),
    paddingTop: hp(2),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: hp(3),
    fontWeight: '700',
    color: theme.colors.text,
    marginBottom: hp(2),
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: hp(3),
  },
  statButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: hp(1.5),
    marginHorizontal: wp(1),
    backgroundColor: theme.colors.grayLight,
    borderRadius: theme.radius.md,
  },
  statButtonSelected: {
    backgroundColor: theme.colors.primary,
  },
  statCount: {
    fontSize: hp(2.2),
    fontWeight: '700',
    color: theme.colors.text,
  },
  statCountSelected: {
    color: 'white',
  },
  statLabel: {
    fontSize: hp(1.6),
    color: theme.colors.textLight,
    marginTop: hp(0.5),
  },
  statLabelSelected: {
    color: 'white',
  },
  listContainer: {
    paddingBottom: hp(4),
  },
  reportItem: {
    backgroundColor: 'white',
    padding: wp(4),
    borderRadius: theme.radius.md,
    marginBottom: hp(1.5),
    borderLeftWidth: 4,
    ...theme.shadows.small,
  },
  reportHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: hp(1),
  },
  reportReason: {
    fontSize: hp(1.8),
    fontWeight: '600',
    color: theme.colors.text,
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: wp(2),
    paddingVertical: hp(0.5),
    borderRadius: theme.radius.sm,
  },
  statusText: {
    color: 'white',
    fontSize: hp(1.4),
    fontWeight: '600',
  },
  postContent: {
    fontSize: hp(1.6),
    color: theme.colors.textLight,
    marginBottom: hp(1),
    lineHeight: hp(2.2),
  },
  reportFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  reporterText: {
    fontSize: hp(1.4),
    color: theme.colors.textLight,
  },
  dateText: {
    fontSize: hp(1.4),
    color: theme.colors.textLight,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: hp(8),
  },
  emptyText: {
    fontSize: hp(2),
    color: theme.colors.textLight,
  },
  loadingMoreContainer: {
    paddingVertical: hp(2),
    alignItems: 'center',
  },
  noMoreData: {
    fontSize: hp(1.8),
    color: theme.colors.textLight,
    textAlign: 'center',
    paddingVertical: hp(2),
  },
});
